// patient-records/buildSummaryReport.js

export const buildSummaryReport = (scaleName, scoreResult, rawAnswers, scaleData, patientProfile, instructionRepeats = {}) => {
  let interpretation = '';

  const normalizedScaleName = String(scaleName || '').trim().toUpperCase();

  const totalScore = typeof scoreResult === 'number' ? scoreResult : (scoreResult?.totalScore || 0);
  // НОВОЕ: rawScore (до поправки за образование) — раньше не извлекался
  // вообще, поправка нигде не была видна врачу, хотя сервер её уже считает.
  const rawScore = scoreResult?.rawScore ?? null;
  const subScores = typeof scoreResult === 'object' ? (scoreResult?.subScores || []) : [];
  const mis = scoreResult?.mis ?? null;
  const misPrognosis = scoreResult?.misPrognosis || '';
  // НОВОЕ: memoryDetail (свободно/с подсказкой/с выбором/не вспомнил) и
  // executiveCompositeIndex уже считаются в moca.py (v1.8), но раньше здесь
  // не извлекались из scoreResult — данные терялись до того, как доходили
  // до TestRunnerPage.
  const memoryDetail = scoreResult?.memoryDetail || null;
  const executiveCompositeIndex = scoreResult?.executiveCompositeIndex ?? null;
  const vlomRatio = scoreResult?.vlomRatio ?? null;
  const isVlomValid = scoreResult?.isVlomValid ?? true;
  const clinicalScenarios = scoreResult?.clinicalScenarios || [];
  const differentialDiagnosis = scoreResult?.differentialDiagnosis || [];
  const ageAdjustedCutoff = scoreResult?.ageAdjustedCutoff || 26;
  const ageNote = scoreResult?.ageNote || '';
  const vigilanceDetail = scoreResult?.vigilanceDetail || null;
  const nihssDetail = scoreResult?.nihssDetail || null;
  // НОВОЕ: оценка ориентировки MoCA (11a–11f) приходит с сервера — по каждому
  // пункту {ok, expected}. Раньше здесь была своя, упрощённая проверка, которая
  // расходилась с сервером (например, «15» засчитывалось за 5-е число), и
  // врач видел «верно/неверно», не совпадающее с реальным баллом.
  const orientationDetail = scoreResult?.orientationDetail || null;
  const ORIENTATION_KEY = { '11a': 'day', '11b': 'weekday', '11c': 'month', '11d': 'year', '11e': 'city', '11f': 'place' };
  const subScoreOf = (marker) => {
    const d = (subScores || []).find(s => String(s.domain || '').startsWith(marker));
    return d ? d.score : null;
  };

  if (normalizedScaleName === 'MOCA') {
    if (totalScore >= ageAdjustedCutoff) interpretation = 'Норма';
    else if (totalScore >= 19) interpretation = 'Лёгкие / умеренные когнитивные нарушения';
    else interpretation = 'Выраженные когнитивные нарушения';
  } else if (normalizedScaleName === 'MMSE') {
    if (totalScore >= 28) interpretation = 'Нет значимых когнитивных нарушений';
    else if (totalScore >= 24) interpretation = 'Лёгкие когнитивные нарушения';
    else if (totalScore >= 18) interpretation = 'Умеренные (лёгкая деменция)';
    else if (totalScore >= 10) interpretation = 'Выраженные (умеренная деменция)';
    else interpretation = 'Тяжёлые (тяжёлая деменция)';
  } else if (normalizedScaleName === 'SDMT' || normalizedScaleName === 'SDMT V2') {
    if (totalScore >= 45) interpretation = 'Норма';
    else if (totalScore >= 44) interpretation = 'Нижняя граница нормы';
    else if (totalScore >= 40) interpretation = 'Лёгкое нарушение';
    else if (totalScore >= 32) interpretation = 'Умеренное нарушение';
    else interpretation = 'Тяжёлое нарушение';
  } else if (normalizedScaleName === 'STROOP') {
 
  } else if (normalizedScaleName === 'NIHSS') {
    if (totalScore === 0) interpretation = 'Нет симптомов';
    else if (totalScore <= 4) interpretation = 'Лёгкий инсульт';
    else if (totalScore <= 15) interpretation = 'Умеренный инсульт';
    else if (totalScore <= 20) interpretation = 'Среднетяжёлый инсульт';
    else interpretation = 'Тяжёлый инсульт';
  } else {
    interpretation = 'Неизвестная шкала';
  }

  // НОВОЕ: строка-сводка для Executive Summary — собирается по шаблону из
  // уже посчитанных полей, врач не редактирует вручную. Только для MoCA,
  // для остальных шкал пусто (эти карточки у них не отображаются).
  //
  // Способ ответа в 6c приходит с сервера в vigilanceDetail.input
  // ('clap' | 'touch' | 'pointer'); нет — считаем хлопками (старые отчёты).
  // Трактовка паттернов валидирована для хлопков; для касания/клика —
  // ориентировочно (решение клиники), поэтому формулировки разные.
  const vigilanceInputMode =
    (vigilanceDetail?.input === 'touch' || vigilanceDetail?.input === 'pointer')
      ? vigilanceDetail.input
      : 'clap';

  const VIGILANCE_PATTERN_TEXT = {
    clap: {
      commission: 'Снижено торможение реакции при сохранной устойчивости внимания (экспериментально).',
      omission: 'Снижена устойчивость внимания при сохранном торможении реакции (экспериментально).',
      mixed: 'Снижены обе системы — устойчивость внимания и торможение реакции (экспериментально).',
    },
    touch: {
      commission: 'Преобладание лишних касаний — предположительное снижение торможения реакции (валидировано для хлопков, ориентировочно).',
      omission: 'Преобладание пропусков — предположительное снижение устойчивости внимания (валидировано для хлопков, ориентировочно).',
      mixed: 'Смешанный паттерн ошибок — предположительно снижены и устойчивость внимания, и торможение (ориентировочно).',
    },
    pointer: {
      commission: 'Преобладание лишних кликов — предположительное снижение торможения реакции (валидировано для хлопков, ориентировочно).',
      omission: 'Преобладание пропусков — предположительное снижение устойчивости внимания (валидировано для хлопков, ориентировочно).',
      mixed: 'Смешанный паттерн ошибок — предположительно снижены и устойчивость внимания, и торможение (ориентировочно).',
    },
  };

  let execSummaryText = '';
  let vigilanceSummaryText = '';

  if (normalizedScaleName === 'MOCA') {
    const parts = [];

    // 1. Балл
    let scoreLine = `MoCA ${totalScore}/30`;
    if (rawScore !== null && rawScore !== totalScore) {
      scoreLine += ` (сырой ${rawScore} + поправка за образование)`;
    }
    scoreLine += ` — ${interpretation}`;
    if (ageAdjustedCutoff && ageAdjustedCutoff !== 26) {
      scoreLine += `, порог нормы снижен до ${ageAdjustedCutoff} (возраст)`;
    }
    parts.push(scoreLine + '.');

    // 2. Память — логика по тому, помогла ли подсказка
    if (memoryDetail) {
      const { freeRecallCount, hintCount, choiceCount } = memoryDetail;
      if (freeRecallCount === 5) {
        parts.push('Память: воспроизведение полное, все слова названы свободно.');
      } else if (hintCount > 0 || choiceCount > 0) {
        parts.push('Память: снижено, эффект подсказки сохранён (дефицит извлечения).');
      } else {
        parts.push('Память: снижено, подсказка не помогает (дефицит кодирования).');
      }
    }

    // 3. Ведущая гипотеза / клинический сценарий — приоритет: конкретная
    // гипотеза > Сценарий Б (органика) > Сценарий В (лобный) > Сценарий А
    // (усталость) > ничего не выявлено.
    if (differentialDiagnosis && differentialDiagnosis.length > 0) {
      const top = differentialDiagnosis[0];
      const confidenceSuffix = top.confidence === 'low' ? ' (низкая уверенность)'
        : top.confidence === 'medium' ? ' (средняя уверенность)' : '';
      parts.push(`Профиль поддерживает гипотезу «${top.diagnosis}»${confidenceSuffix}.`);
    } else if (clinicalScenarios.some(s => s.includes('Сценарий Б'))) {
      parts.push('Мультимодальное снижение — рекомендовано углублённое обследование.');
    } else if (clinicalScenarios.some(s => s.includes('Сценарий В'))) {
      parts.push('Изолированный лобный дефицит при сохранной памяти — рекомендована нейровизуализация.');
    } else if (clinicalScenarios.some(s => s.includes('Сценарий А'))) {
      parts.push('Вероятно функциональное снижение — рекомендован повтор через 3–6 месяцев.');
    } else {
      parts.push('Патологических паттернов не выявлено.');
    }

    execSummaryText = parts.join(' ');

    // Бдительность — только если паттерн реально повлиял на балл
    // (commission/omission/mixed); единичная ошибка ("isolated") или их
    // отсутствие в сводке не упоминаются — балл в этих случаях не теряется.
    // Правило показа не зависит от способа ответа; меняются только слова.
    const vigilanceTexts = VIGILANCE_PATTERN_TEXT[vigilanceInputMode];
    if (vigilanceDetail?.pattern && vigilanceTexts[vigilanceDetail.pattern]) {
      vigilanceSummaryText = vigilanceTexts[vigilanceDetail.pattern];
    }
  }

  const today = new Date();
  const days = ['воскресенье', 'понедельник', 'вторник', 'среда', 'четверг', 'пятница', 'суббота'];
  const months = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август', 'сентябрь', 'октябрь', 'ноябрь', 'декабрь'];

  // Зеркально api/app/scoring/moca.py::_filter_fluency_words — менять СИНХРОННО.
  const FLUENCY_ROOT_LEN = 4;
  const FLUENCY_NUM_RE = /^\d+$|^сто$|[а-я]+десят$|[а-я]+сот$|^девяносто$|^сорок$/;
  const FLUENCY_NAMES = new Set([
    'катя', 'катерина', 'кирилл', 'костя', 'константин', 'клава', 'клавдия',
    'кира', 'клим', 'ксения', 'кузьма', 'коваль', 'ковальский', 'крылов',
    'керимов', 'кизимов', 'кристина','коля','кеша', "колокольцев",
  ]);

  function fluencyGrams(w) {
    const s = new Set();
    for (let i = 0; i + FLUENCY_ROOT_LEN <= w.length; i++) s.add(w.slice(i, i + FLUENCY_ROOT_LEN));
    return s;
  }

  function filterFluencyWords(words) {
    const kept = [], keptGrams = new Set();
    for (const w of words) {
      if (!w.startsWith('к') || w.length <= 1) continue;
      if (FLUENCY_NUM_RE.test(w) || FLUENCY_NAMES.has(w)) continue;
      const g = fluencyGrams(w);
      let related = false;
      for (const x of g) { if (keptGrams.has(x)) { related = true; break; } }
      if (related) continue;
      kept.push(w);
      for (const x of g) keptGrams.add(x);
    }
    return kept;
  }

  const detailedAnswers = scaleData.questions.filter(q => !q.isTrial).map(q => {
    const pAns = String(rawAnswers[q.id] || '');
    let cAns = '';
    let isCorrect = null;
    let points = null;
    let patientAnswer = pAns;
    let note = '';

    if (q.type === 'drawing' || q.isManualAction) {
      if (q.id === '2a') note = 'Качественный анализ: искажение перспективы (DLB/теменная), стереотипия/персеверация (лобная).';
      if (q.id === '3a') note = 'Качественный анализ: 1) Нарушение расстановки цифр (DLB/БА), 2) Ошибки стрелок (лобная), 3) Импульсивность (bvFTD).';

      return {
        id: q.id,
        text: q.ask,
        patientAnswer: q.type === 'drawing' ? 'См. рисунок' : (pAns || 'Ожидает оценки врача'),
        image: q.type === 'drawing' ? rawAnswers[q.id] : null,
        isCorrect: null,
        needsManualScore: true,
        maxScore: q.maxScore || 1,
        points: null,
        note,
        repeats: instructionRepeats[q.id] || 0
      };
    }

    // Проба с камерой в ШКАЛЕ: один повтор, без «верно/неверно». В выписку
    // идёт наблюдение CV-модуля — что именно зафиксировано, — а трактует
    // его врач. Балльной оценки здесь намеренно нет: NIHSS даёт один балл
    // 0-3 на весь пункт 4, а строк в шкале три (4a, 4b, 4c), и выпадающий
    // список у каждой утроил бы оценку пункта.

        // 1а NIHSS — уровень бодрствования с эскалацией. Фронт пишет JSON
    // {outcome, attempt, answer?, points}. Балл: 0 — сразу, 1 — со 2-й,
    // 2 — с 3-й, 3 — нет реакции (больше = хуже, как в NIHSS).
      if (q.type === 'consciousness') {
      let display = pAns || 'Нет данных';
      try {
        const data = JSON.parse(pAns);
        if (data && data.outcome === 'answered') {
          display = `Ответил с попытки ${data.attempt || 1}: «${data.answer || ''}»`;
        } else if (data && data.outcome === 'no_response') {
          display = 'Нет реакции на обращения';
        }
      } catch {
        if (pAns) display = `Ответил (кнопка): «${pAns}»`;
      }
      return {
        id: q.id,
        text: q.ask,
        patientAnswer: display,
        correctAnswer: 'Любой ответ с 1-й попытки',
        isCorrect: null,
        points: subScoreOf('1а'),
        maxScore: 3,
        note: 'Балл: 0 — ответил сразу; +1 за каждую следующую попытку; 3 — нет реакции после всех попыток. Чем выше балл, тем хуже. Считает сервер.',
        repeats: instructionRepeats[q.id] || 0
      };
    }
    
    if (q.type === 'cv_face_action') {
      let observed = 'Нет данных';
      try {
        const data = JSON.parse(pAns);
        const quality = Number.isFinite(data.category) ? `категория ${data.category}` : null;
        const issues = Array.isArray(data.observations) && data.observations.length
          ? `замечания: ${data.observations.join(', ')}`
          : 'замечаний нет';
        observed = [quality, issues].filter(Boolean).join('; ');
      } catch {
        observed = pAns || 'Проба не выполнена';
      }

      return {
        id: q.id,
        text: q.ask,
        patientAnswer: observed,
        correctAnswer: 'Оценка врача',
        isCorrect: null,
        points: null,
        note: 'Проба с камерой: фиксируется выполнение, без автоматической оценки.',
        repeats: instructionRepeats[q.id] || 0
      };
    }
    // NIHSS: 1б — ОДИН пункт шкалы (месяц + возраст, 0-2 = число неверных).
    // Строки 1b и 1c объединяются; балл берём из серверного subScores —
    // сервер проверяет месяц по дате, а возраст по профилю из БД.
      if (normalizedScaleName === 'NIHSS' && q.id === '1b') {
      const d = nihssDetail?.item1b || null;
      const verdict = (ok) => (ok === true ? 'верно' : ok === false ? 'неверно' : 'не проверялся');
      const monthRaw = rawAnswers['1b'] || '—';
      const ageRaw = rawAnswers['1c'] || '—';
      const expectedMonth = d?.expectedMonth || months[today.getMonth()];
      const expectedAge = d?.expectedAge ?? patientProfile?.age ?? null;
      const ageUnknown = expectedAge === null || expectedAge === undefined;
      return {
        id: '1б',
        text: 'Какой сейчас месяц? Сколько вам лет?',
        patientAnswer: d
          ? `Месяц: ${monthRaw} (${verdict(d.monthOk)}); возраст: ${ageRaw} (${verdict(d.ageOk)})`
          : `Месяц: ${monthRaw}; возраст: ${ageRaw}`,
        correctAnswer: `месяц: ${expectedMonth}; возраст: ${ageUnknown ? 'не указан в профиле' : expectedAge}`,
        isCorrect: null,
        points: subScoreOf('1б'),
        maxScore: 2,
        note: ageUnknown
          ? 'Возраст пациента не указан в профиле — оценён только месяц, возраст проверьте вручную.'
          : 'Оба верно — 0; один верно — 1; ни одного — 2. Чем выше балл, тем хуже. Считает сервер.',
        repeats: (instructionRepeats['1b'] || 0) + (instructionRepeats['1c'] || 0)
      };
    }
    if (normalizedScaleName === 'NIHSS' && q.id === '1c') {
      return null; // возраст входит в строку пункта 1б выше
    }
    
    const orientItem = (normalizedScaleName === 'MOCA' && orientationDetail)
      ? orientationDetail[ORIENTATION_KEY[q.id]] : null;

    if (orientItem) {
      // Верно/неверно и ожидаемый ответ — как посчитал сервер
      isCorrect = !!orientItem.ok;
      cAns = orientItem.expected;
      points = isCorrect ? 1 : 0;
      note = ORIENTATION_KEY[q.id] === 'place'
        ? 'Верными считаются: клиника, поликлиника, больница. Проверено сервером.'
        : ORIENTATION_KEY[q.id] === 'city'
          ? 'Проверено сервером.'
          : 'Проверено сервером по текущей дате; учитываются словесные формы и падежи (например, «четырнадцатое», «в среду»).';
    }
    else if (q.calendarCheck) {
      note = 'Проверка по текущей дате. Учитывайте словесные формы (например, "первое" вместо "1").';
      const dayWords = {
        1: 'перв', 2: 'втор', 3: 'трет', 4: 'четверт', 5: 'пят', 6: 'шест', 7: 'седьм', 8: 'восьм', 9: 'девят', 10: 'десят',
        11: 'одиннадцат', 12: 'двенадцат', 13: 'тринадцат', 14: 'четырнадцат', 15: 'пятнадцат', 16: 'шестнадцат', 17: 'семнадцат', 18: 'восемнадцат', 19: 'девятнадцат', 20: 'двадцат',
        21: 'двадцать перв', 22: 'двадцать втор', 23: 'двадцать трет', 24: 'двадцать четверт', 25: 'двадцать пят', 26: 'двадцать шест', 27: 'двадцать седьм', 28: 'двадцать восьм', 29: 'двадцать девят', 30: 'тридцат', 31: 'тридцать перв'
      };

      if (q.calendarCheck === 'year') {
        const yearStr = String(today.getFullYear());
        const yearWord = yearStr.endsWith('26') ? 'двадцать шест' : yearStr;
        isCorrect = pAns.includes(yearStr) || pAns.toLowerCase().includes(yearWord);
        cAns = yearStr;
      }
      else if (q.calendarCheck === 'month') {
        const cm = months[today.getMonth()];
        isCorrect = pAns.toLowerCase().includes(cm) || pAns.toLowerCase().includes(cm.substring(0, 3));
        cAns = cm;
      }
      else if (q.calendarCheck === 'day') {
        const dayStr = String(today.getDate());
        const dayWord = dayWords[today.getDate()] || '';
        const dayWordShort = dayWord.substring(0, 5);
        isCorrect = pAns.includes(dayStr) || pAns.toLowerCase().includes(dayWordShort);
        cAns = dayStr;
      }
      else if (q.calendarCheck === 'weekday') {
        const cd = days[today.getDay()];
        isCorrect = pAns.toLowerCase().includes(cd) || pAns.toLowerCase().includes(cd.substring(0, 3));
        cAns = cd;
      }
      points = isCorrect ? 1 : 0;
    }

    else if (q.type === 'voice_serial') {
      cAns = '93, 86, 79, 72, 65';
      note = 'Учитываются только 3 правильных ответа (по 1 баллу за каждый). Ошибки на 4-м шаге не штрафуются.';
      const match = pAns.match(/Балл: (\d+) из (\d+)/);
      if (match) {
        points = parseInt(match[1]);
        isCorrect = points === parseInt(match[2]);
      } else {
        points = 0;
        isCorrect = false;
      }
    }
    else if (q.type === 'trail_making') {
      if (pAns.includes('||')) {
        const [answerText, dataUrl] = pAns.split('||');
        isCorrect = answerText === 'Задание выполнено верно';
        cAns = 'Без ошибок (1-А-2-Б...)';
        points = isCorrect ? 1 : 0;
        patientAnswer = isCorrect ? 'Задание выполнено верно' : 'Задание выполнено неверно';
        return {
          id: q.id,
          text: q.ask,
          patientAnswer: 'См. рисунок',
          image: dataUrl,
          isCorrect: isCorrect,
          needsManualScore: false,
          maxScore: q.maxScore || 1,
          points: points,
          note: 'Качественный анализ: 1) Ошибка последовательности (внимание), 2) Потеря правила чередования (лобная), 3) Зрительно-простр. ошибки (DLB).',
          repeats: instructionRepeats[q.id] || 0
        };
      }
      isCorrect = pAns === 'Задание выполнено верно';
      cAns = 'Без ошибок (1-А-2-Б...)';
      points = isCorrect ? 1 : 0;
      note = 'Качественный анализ: 1) Ошибка последовательности (внимание), 2) Потеря правила чередования (лобная), 3) Зрительно-простр. ошибки (DLB).';
    }
    else if (q.type === 'vigilance') {
      const vData = rawAnswers[q.id] && typeof rawAnswers[q.id] === 'object' ? rawAnswers[q.id] : {};
      const hits = Number(vData.hits) || 0;
      const commissions = Number(vData.commissions) || 0;
      const omissions = Number(vData.omissions) || 0;
      const totalErrors = commissions + omissions;
      // Способ ответа: 'clap' (по умолчанию, в т.ч. для старых отчётов без
      // input) | 'touch' (касание экрана) | 'pointer' (клик мышью).
      const inputMode = (vData.input === 'touch' || vData.input === 'pointer') ? vData.input : 'clap';

      points = totalErrors <= 1 ? 1 : 0;
      isCorrect = points === 1;
      cAns = '0-1 ошибка = 1 балл';

      if (inputMode === 'touch') {
        patientAnswer = `Верных касаний: ${hits}, лишних касаний: ${commissions}, пропусков: ${omissions} (способ ответа: касание экрана)`;
      } else if (inputMode === 'pointer') {
        patientAnswer = `Верных ответов: ${hits}, лишних кликов: ${commissions}, пропусков: ${omissions} (способ ответа: клик мышью)`;
      } else {
        patientAnswer = `Верных хлопков: ${hits}, лишних хлопков: ${commissions}, пропусков: ${omissions}`;
      }

      // Приставка трактовки: для касания/клика — оговорка про валидацию.
      const qualPrefix = inputMode === 'clap'
        ? 'Качественный анализ (экспериментально)'
        : `Качественный анализ (экспериментально; валидировано для хлопков, при ${inputMode === 'touch' ? 'касании' : 'клике'} — ориентировочно)`;
      const extraNoun = inputMode === 'touch' ? 'касания' : inputMode === 'pointer' ? 'клики' : 'хлопки';

      if (totalErrors === 0) {
        note = 'Ошибок нет.';
      } else if (totalErrors === 1) {
        // Синхронно с серверным паттерном "isolated": балл не теряется.
        note = 'Единичная ошибка одного типа — изолированная, без клинической трактовки (на балл не влияет).';
      } else if (commissions >= 2 && omissions === 0) {
        note = `${qualPrefix}: преобладают лишние ${extraNoun} — паттерн, ассоциируемый с импульсивностью/нарушением торможения (лобная дисфункция).`;
      } else if (omissions >= 2 && commissions === 0) {
        note = `${qualPrefix}: преобладают пропуски — паттерн, ассоциируемый со снижением устойчивого внимания.`;
      } else {
        note = `${qualPrefix}: смешанный паттерн ошибок (и лишние ${extraNoun}, и пропуски).`;
      }
    }
    else if (q.type === 'coding' || q.type === 'coding_v2') {
      try {
        const data = JSON.parse(pAns);
        points = data.score || 0;
        isCorrect = points === q.sequence.length;
        cAns = 'Норма: 45+ баллов';
        patientAnswer = `Балл: ${data.score}, Ошибки: ${data.errors}, Пропуски: ${data.omissions}. Динамика (30с): [${data.intervals?.join(', ')}]`;
      } catch {
        points = 0;
        isCorrect = false;
        cAns = 'Норма: 45+ баллов';
        patientAnswer = 'Ошибка чтения данных теста';
      }
    }
    else if (q.type === 'stroop') {
      try {
        const data = JSON.parse(pAns);
        points = data.interference <= 15 ? 1 : 0;
        isCorrect = points === 1;
        cAns = 'Норма интерференции: <=15с';
        patientAnswer = `Время A: ${data.times[0]}с, B: ${data.times[1]}с, C: ${data.times[2]}с. Интерференция: ${data.interference}с. Ошибки C: ${data.errors}`;
      } catch {
        points = 0; isCorrect = false; cAns = '-'; patientAnswer = 'Ошибка данных';
      }
    }
    else if (q.type === 'memory' || q.type === 'memory_cascade') {
      cAns = q.words ? `${q.words.length} слов` : '5 слов';
      note = 'Качественный анализ: Не улучшается с подсказкой = дефицит кодирования (БА). Улучшается = дефицит извлечения (VaD/DLB).';

      if (q.id === '5a' || q.id === '5b' || q.id === '5c') {
        points = 0;
        isCorrect = null;
        cAns = 'Пробное задание';
        patientAnswer = 'Чтение слов';
      } 
      else if (Array.isArray(rawAnswers[q.id])) {
        const ansArray = rawAnswers[q.id];
        const freeCount = ansArray.filter(i => i.status === 'free').length;
        const hintCount = ansArray.filter(i => i.status === 'hint').length;
        const choiceCount = ansArray.filter(i => i.status === 'choice').length;
        const failCount = ansArray.filter(i => i.status === 'fail').length;

        points = freeCount;
        isCorrect = points === 5;
        patientAnswer = `Свободно: ${freeCount}, с подсказкой (категория): ${hintCount}, с выбором: ${choiceCount}, не вспомнил: ${failCount}`;
      } else {
        const match = pAns.match(/Вспомнил: (\d+) из (\d+)/);
        if (match) {
          points = parseInt(match[1]);
          isCorrect = points === parseInt(match[2]);
        } else {
          points = 0;
          isCorrect = false;
        }
      }
    }
    else if (q.validAnswer) {
      // Для 9b/9c используем regex, идентично бэкенду
      if (q.id === '9b' || q.id === '9c') {
        const ansLower = pAns.toLowerCase();
        if (q.id === '9b') {
          cAns = 'Транспорт / Средства передвижения';
          isCorrect = /транспорт|средства передвижения|ездить/.test(ansLower);
        } else {
          cAns = 'Измерительные приборы';
          // ИСПРАВЛЕНО: было "измерительн|измерени|прибор|термометр" — покрывало
          // только существительное/прилагательное, но не глаголы ("измеряют",
          // "измерять"). Сокращено до общего корня "измер", который покрывает
          // все формы разом; "прибор"/"термометр" убраны, т.к. их не было в
          // бэкенде (то самое расхождение дисплея и реального балла).
          isCorrect = /измер/.test(ansLower);
        }
        points = isCorrect ? 1 : 0;
      } else if (q.id === '7a' || q.id === '7b') {
        // Для 7a/7b токенизируем ответ и проверяем КАЖДОЕ слово
        const words_7a = ["я", "знаю", "только", "одно", "что", "иван", "это", "тот", "кто", "может", "сегодня", "помочь"];
        const words_7b = ["кошка", "всегда", "пряталась", "под", "диван", "когда", "собаки", "были", "в", "комнате"];
        const expectedWords = q.id === '7a' ? words_7a : words_7b;
        
        cAns = expectedWords.join(' ');
        // Разбиваем ответ пациента на слова (токены), чтобы избежать ложных срабатываний подстрок
        const patientTokens = pAns.toLowerCase().split(/\s+/).filter(Boolean);
        const allWordsMatch = expectedWords.every(w => patientTokens.includes(w));
        
        isCorrect = allWordsMatch;
        points = isCorrect ? 1 : 0;
      } else {
        // Старая логика для остальных вопросов
        cAns = Array.isArray(q.validAnswer) ? q.validAnswer.join(', ') : q.validAnswer;
        
        if (q.type === 'voice_hidden' && q.ask) {
          if (q.ask.includes('Повторите предложение: ')) {
            cAns = q.ask.replace('Повторите предложение: ', '');
          } else if (q.ask.includes('Повторите фразу: ')) {
            cAns = q.ask.replace('Повторите фразу: ', '');
          }
        }

        if (q.type === 'voice_hidden' && q.ask.includes('Повторите предложение: ')) {
          cAns = q.ask.replace('Повторите предложение: ', '');
        }

        if (Array.isArray(q.validAnswer)) {
          const matchesWords = q.validAnswer.filter(w => pAns.toLowerCase().includes(String(w).toLowerCase())).length;
          const allWordsMatch = matchesWords === q.validAnswer.length;

          let digitsMatch = false;
          if (q.checkDigits) {
            // ИСПРАВЛЕНО: \b не работает с кириллицей в JS (распознаёт только
            // латиницу как "слова") — слова вида "два", "один" никогда не
            // заменялись. Токенизируем по пробелам и сравниваем целыми
            // токенами. Из каждого токена убираем всё, кроме кириллических
            // букв и цифр — иначе токен со знаком препинания ("восемь,")
            // не совпадёт со словарным ключом ("восемь") и просто потеряется.
            const w2d = {'ноль':'0','нуль':'0','один':'1','одна':'1','два':'2','две':'2','три':'3','четыре':'4','пять':'5','шесть':'6','семь':'7','восемь':'8','девять':'9'};
            const tokens = pAns.toLowerCase().split(/\s+/).map(t => t.replace(/[^а-яё0-9]/g, ''));
            const mappedTokens = tokens.map(t => w2d[t] || t);
            let parseableAns = mappedTokens.join(' ');

            // Известный артефакт STT: "восемь" иногда распознаётся как "во" +
            // "7" (два токена, причём вторая цифра неверная) — эту замену
            // нужно делать ПОСЛЕ соединения токенов через пробел, иначе "во"
            // и "7" из разных токенов не окажутся рядом друг с другом.
            parseableAns = parseableAns.replace(/во\s*7/g, '8');
            parseableAns = parseableAns.replace(/восем/g, '8');

            const digitsInAnswer = parseableAns.replace(/\D/g, '');
            const uniqueDigits = [...new Set(digitsInAnswer.split(''))].join('');
            digitsMatch = uniqueDigits === q.checkDigits;
            if (uniqueDigits) {
              patientAnswer = uniqueDigits;
            }
          }

          const isFullyCorrect = allWordsMatch || digitsMatch;

          if (q.matchType === 'all') {
            isCorrect = isFullyCorrect;
            points = q.maxScore && q.maxScore > 1 ? matchesWords : (isCorrect ? 1 : 0);
          } else {
            points = (matchesWords > 0 || digitsMatch) ? 1 : 0;
            isCorrect = points === 1;
          }
        } else {
          isCorrect = pAns.toLowerCase().includes(String(q.validAnswer).toLowerCase());
          points = isCorrect ? 1 : 0;
        }
      }
    }
      else if (q.countWords) {
      const rawText = pAns.toLowerCase().replace(/[^а-яё\s]/g, '').trim();
      const validWords = filterFluencyWords(rawText.split(/\s+/).filter(Boolean));

      points = validWords.length >= q.countWords ? 1 : 0;
      isCorrect = points === 1;
      cAns = `>= ${q.countWords} слов на букву "К"`;
      patientAnswer = `Зачтено (на "К", уникальных): ${validWords.length}. Слова: ${validWords.join(', ') || '—'}`;
      note = 'Исключены числа, имена собственные и однокоренные (общая последовательность 4+ букв). Качественный анализ: персеверации, нарушение правила, семантические кластеры (БА).';
    }
    else if (q.target) {
      cAns = q.target;
      isCorrect = pAns.toLowerCase().includes(String(q.target).toLowerCase());
      points = isCorrect ? 1 : 0;
    }
    else {
      cAns = 'Клиническая оценка';
      isCorrect = null;
      points = null;
    }

    return { 
      id: q.id, 
      text: q.ask, 
      patientAnswer, 
      correctAnswer: cAns, 
      isCorrect, 
      points, 
      note, 
      repeats: instructionRepeats[q.id] || 0
    };
    }).filter(Boolean);

  return {
    scaleName,
    date: new Date().toLocaleDateString('ru-RU'),
    totalScore,
    rawScore,
    interpretation,
    execSummaryText,
    vigilanceSummaryText,
    subScores,
    detailedAnswers,
    mis,
    misPrognosis,
    memoryDetail,
    executiveCompositeIndex,
    vlomRatio,
    isVlomValid,
    ageAdjustedCutoff,
    ageNote,
    vigilanceDetail,
    clinicalScenarios,
    differentialDiagnosis
  };
};